var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { withPreferredFirst } from "./selectMappers";
var RULES = [
    {
        primaryContentType: "01",
        form150: [{ kind: "prefix", value: "A" }],
        formDetail175: [{ kind: "prefix", value: "A" }],
    },
    {
        primaryContentType: "10",
        form150: [
            { kind: "prefix", value: "B" },
            { kind: "prefix", value: "E" },
            { kind: "prefix", value: "D" },
        ],
        formDetail175: [
            { kind: "prefix", value: "B" },
            { kind: "prefix", value: "E" },
        ],
    },
    {
        primaryContentType: "11",
        form150: [{ kind: "exact", value: "PI" }],
        formDetail175: [
            { kind: "prefix", value: "B" },
            { kind: "prefix", value: "E" },
        ],
    },
    {
        primaryContentType: "12",
        form150: [
            { kind: "prefix", value: "B" },
            { kind: "prefix", value: "C" },
            { kind: "prefix", value: "E" },
        ],
        formDetail175: [
            { kind: "prefix", value: "B" },
            { kind: "prefix", value: "C" },
            { kind: "prefix", value: "E" },
        ],
    },
];
var getRulePatterns = function (primaryContentType, register) {
    if (!primaryContentType)
        return null;
    var rule = RULES.find(function (r) { return r.primaryContentType === primaryContentType; });
    if (!rule)
        return null;
    return register === "form" ? rule.form150 : rule.formDetail175;
};
var matches = function (value, patterns) {
    return patterns.some(function (p) {
        return p.kind === "exact" ? value === p.value : value.startsWith(p.value);
    });
};
export var isAllowedByPrimaryContentType = function (value, primaryContentType, register) {
    if (!value)
        return true;
    var patterns = getRulePatterns(primaryContentType, register);
    return patterns === null || matches(value, patterns);
};
export var filterByPrimaryContentType = function (items, primaryContentType, register) {
    var patterns = getRulePatterns(primaryContentType, register);
    if (patterns === null)
        return __spreadArray([], items, true);
    return items.filter(function (item) { return item.value !== undefined && matches(item.value, patterns); });
};
/**
 * ONIX Product form (codetable 150) groups that restrict the Product form
 * detail (codetable 175) selection to the same initial letter: a form starting
 * with "A" (audio) allows only A* details, "B" (book) only B* details and "E"
 * (digital) only E* details. Any other form keeps the full detail selection.
 */
var FORM_GROUPS_RESTRICTING_DETAIL = ["A", "B", "E"];
var getFormDetailGroup = function (formCode) {
    var group = formCode === null || formCode === void 0 ? void 0 : formCode.charAt(0);
    return group && FORM_GROUPS_RESTRICTING_DETAIL.includes(group) ? group : null;
};
/**
 * Tells whether a Product form detail (codetable 175) code is compatible with
 * the selected Product form (codetable 150).
 *
 * @param detailCode the form detail code; an empty value is always allowed
 * @param formCode the selected form code; empty or outside the A/B/E groups means no restriction
 * @returns {@code true} when the detail may be combined with the form
 */
export var isFormDetailAllowedByForm = function (detailCode, formCode) {
    if (!detailCode)
        return true;
    var group = getFormDetailGroup(formCode);
    return group === null || detailCode.startsWith(group);
};
/** Product form (codetable 150) group of digital / e-book forms (EA, EB, EC, ED). */
var EBOOK_FORM_GROUP = "E";
/**
 * Preferred Product form detail (codetable 175) file formats of an e-book form,
 * mirroring the one-off ISBN/ISMN request: E107 PDF, E101 EPUB, E127 Mobipocket
 * and E100 other format, in that order. An e-book form offers every E* detail
 * (see {@link filterFormDetailsByForm}); these formats are only hoisted to the
 * top of the picker, followed by the remaining E* details in codetable order.
 */
export var EBOOK_FORM_DETAIL_CODES = [
    "E107",
    "E101",
    "E127",
    "E100",
];
/**
 * Tells whether a Product form (codetable 150) code is an e-book form (E group).
 *
 * @param formCode the form code
 * @returns {@code true} for EA, EB, EC, ED and any other E* form
 */
export var isEbookForm = function (formCode) {
    return getFormDetailGroup(formCode) === EBOOK_FORM_GROUP;
};
/**
 * Restricts the Product form detail (codetable 175) items to those offered for
 * the selected Product form (codetable 150). Meant to be applied on top of
 * {@link filterByPrimaryContentType}, so the offered details are the
 * intersection of both restrictions. An A, B or E form offers the same-letter
 * details; for an e-book (E) form the preferred formats
 * {@link EBOOK_FORM_DETAIL_CODES} that are present come first in their order,
 * followed by the remaining E* details in codetable order.
 *
 * @param items form detail codetable items
 * @param formCode the selected form code; empty or outside the A/B/E groups keeps every item
 * @returns a new array with the offered items
 */
export var filterFormDetailsByForm = function (items, formCode) {
    var group = getFormDetailGroup(formCode);
    if (group === null)
        return __spreadArray([], items, true);
    var sameGroup = items.filter(function (item) { return item.value !== undefined && item.value.startsWith(group); });
    return group === EBOOK_FORM_GROUP
        ? withPreferredFirst(sameGroup, EBOOK_FORM_DETAIL_CODES)
        : sameGroup;
};
/**
 * Derives the primary content type shared by the products already attached to a
 * title. All products of one title must share the same primary content type, so
 * the first existing product's type is authoritative. Returns {@code undefined}
 * when the title has no products yet (type is then freely selectable).
 *
 * @param products products currently attached to the title
 * @returns the locked primary content type, or {@code undefined} when none exists
 */
export var getLockedPrimaryContentType = function (products) { var _a; return (_a = products === null || products === void 0 ? void 0 : products.find(function (product) { return product.primaryContentType; })) === null || _a === void 0 ? void 0 : _a.primaryContentType; };
